<script setup>
import { computed } from 'vue';
import { useMemoize } from '@vueuse/core';

import getDay from 'date-fns/getDay';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';

import { getQuantileIntervals } from '@chatwoot/utils';

import { groupHeatmapByDay } from 'helpers/ReportsDataHelper';
import { useI18n } from 'vue-i18n';
import { useHeatmapTooltip } from './composables/useHeatmapTooltip';
import HeatmapTooltip from './HeatmapTooltip.vue';

const props = defineProps({
  heatmapData: {
    type: Array,
    default: () => [],
  },
  numberOfRows: {
    type: Number,
    default: 7,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  colorScheme: {
    type: String,
    default: 'blue',
    validator: value => ['blue', 'green'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
});
const { t, locale } = useI18n();

const dataRows = computed(() => {
  const groupedData = groupHeatmapByDay(props.heatmapData);
  return Array.from(groupedData.keys()).map(dateKey => {
    const rowData = groupedData.get(dateKey);
    return {
      dateKey,
      data: rowData,
      dataHash: rowData.map(d => d.value).join(','),
    };
  });
});

const quantileRange = computed(() => {
  const flattendedData = props.heatmapData.map(data => data.value);
  return getQuantileIntervals(flattendedData, [0.2, 0.4, 0.6, 0.8, 0.9, 0.99]);
});

const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(String(locale.value).replace('_', '-'), {
      day: 'numeric',
      month: 'short',
    })
);
function formatDate(dateString) {
  return dateFormatter.value.format(new Date(dateString));
}

const DAYS_OF_WEEK = [
  t('DAYS_OF_WEEK.SUNDAY'),
  t('DAYS_OF_WEEK.MONDAY'),
  t('DAYS_OF_WEEK.TUESDAY'),
  t('DAYS_OF_WEEK.WEDNESDAY'),
  t('DAYS_OF_WEEK.THURSDAY'),
  t('DAYS_OF_WEEK.FRIDAY'),
  t('DAYS_OF_WEEK.SATURDAY'),
];

function getDayOfTheWeek(date) {
  const dayIndex = getDay(date);

  return DAYS_OF_WEEK[dayIndex];
}

// D-R1 + 7-8 = A: six steps of the kit's series colour (chart-1 for traffic, chart-3 for resolutions) mixed with the
// card surface, no new colour; an empty hour is the surface with a border. They follow the theme by themselves.
const EMPTY_CELL = 'border border-solid border-polaris-border bg-polaris-card';
const COLOR_SCHEMES = {
  blue: [
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-1)_18%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-1)_32%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-1)_48%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-1)_64%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-1)_82%,var(--polaris-card))]',
    'bg-[color:var(--polaris-chart-1)]',
  ],
  green: [
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-3)_18%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-3)_32%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-3)_48%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-3)_64%,var(--polaris-card))]',
    'bg-[color:color-mix(in_oklab,var(--polaris-chart-3)_82%,var(--polaris-card))]',
    'bg-[color:var(--polaris-chart-3)]',
  ],
};

// Memoized function to calculate CSS class for heatmap cell intensity levels
const getHeatmapLevelClass = useMemoize(
  (value, quantileRangeArray, colorScheme) => {
    if (!value) return EMPTY_CELL;
    let level = [...quantileRangeArray, Infinity].findIndex(
      range => value <= range && value > 0
    );

    if (level > 6) level = 5;

    if (level === 0) {
      return EMPTY_CELL;
    }

    return COLOR_SCHEMES[colorScheme][level - 1];
  }
);

function getHeatmapClass(value) {
  return getHeatmapLevelClass(value, quantileRange.value, props.colorScheme);
}

const countText = value => {
  if (!value)
    return t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.NO_CONVERSATIONS');
  if (value === 1)
    return t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.CONVERSATION', {
      count: value,
    });
  return t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.CONVERSATIONS', {
    count: value,
  });
};
const cellLabel = (dateKey, data) =>
  t('POLARIS_REPORTS.HEATMAP.CELL', {
    day: getDayOfTheWeek(new Date(dateKey)),
    date: formatDate(dateKey),
    hour: new Date(data.timestamp * 1000).getHours(),
    count: countText(data.value),
  });
// What a screen reader gets for the grid: its size and the busiest hour (the CSV of the download is the full table).
const summary = computed(() => {
  const peak = props.heatmapData.reduce(
    (best, item) => (item.value > (best?.value ?? 0) ? item : best),
    null
  );
  const size = t('POLARIS_REPORTS.HEATMAP.SIZE', {
    title: props.title,
    days: dataRows.value.length,
  });
  if (!peak) return size;
  const at = new Date(peak.timestamp * 1000);
  return `${size} ${t('POLARIS_REPORTS.HEATMAP.PEAK', {
    day: getDayOfTheWeek(at),
    date: formatDate(at),
    hour: at.getHours(),
    count: countText(peak.value),
  })}`;
});

// Tooltip composable
const tooltip = useHeatmapTooltip();
</script>

<!-- eslint-disable vue/no-static-inline-styles -->
<!-- The grid scrolls inside its card on narrow screens (24 columns need 700px), never the page. -->
<template>
  <div class="w-full min-w-0 overflow-x-auto" data-heatmap-grid>
    <div
      class="grid relative w-full min-w-[820px] gap-x-4 gap-y-2.5 grid-cols-[104px_1fr]"
    >
      <template v-if="isLoading">
        <div class="grid gap-[5px] flex-shrink-0">
          <Skeleton
            v-for="ii in numberOfRows"
            :key="ii"
            class="w-full h-8 min-w-[70px] rounded-polaris-sm"
          />
        </div>
        <div class="grid gap-[5px] w-full">
          <div
            v-for="ii in numberOfRows"
            :key="ii"
            class="grid gap-[5px] grid-cols-[repeat(24,_1fr)]"
          >
            <Skeleton
              v-for="jj in 24"
              :key="jj"
              class="w-full h-8 rounded-polaris-sm"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="grid gap-[5px] flex-shrink-0" aria-hidden="true">
          <div
            v-for="row in dataRows"
            :key="row.dateKey"
            v-memo="[row.dateKey, locale]"
            class="h-8 min-w-0 whitespace-nowrap text-[11px] leading-tight font-medium text-polaris-foreground flex flex-col items-end justify-center"
          >
            {{ getDayOfTheWeek(new Date(row.dateKey)) }}
            <time class="font-normal text-polaris-muted-foreground">
              {{ formatDate(row.dateKey) }}
            </time>
          </div>
        </div>
        <div
          class="grid gap-[5px] w-full"
          style="content-visibility: auto"
          role="img"
          :aria-label="summary"
        >
          <div
            v-for="row in dataRows"
            :key="row.dateKey"
            v-memo="[row.dataHash, colorScheme, locale]"
            class="grid gap-[5px] grid-cols-[repeat(24,_1fr)]"
            style="content-visibility: auto"
          >
            <div
              v-for="data in row.data"
              :key="data.timestamp"
              class="h-8 rounded-polaris-sm cursor-pointer"
              :class="getHeatmapClass(data.value)"
              :aria-label="cellLabel(row.dateKey, data)"
              @mouseenter="tooltip.show($event, data.value)"
              @mouseleave="tooltip.hide"
            />
          </div>
        </div>
      </template>
      <div />
      <div
        class="grid grid-cols-[repeat(24,_1fr)] gap-[5px] w-full text-[10px] font-medium h-5 text-polaris-muted-foreground"
        aria-hidden="true"
      >
        <div
          v-for="ii in 24"
          :key="ii"
          class="flex items-center justify-center"
        >
          {{ ii - 1 }}
        </div>
      </div>

      <HeatmapTooltip
        :visible="tooltip.visible.value"
        :x="tooltip.x.value"
        :y="tooltip.y.value"
        :value="tooltip.value.value"
      />
    </div>
  </div>
</template>
